import { Injectable } from "@angular/core";

@Injectable({
  providedIn: 'root'
})
export class TextCleanUpService {

  /**
   * Creates a sample string for language detection from extracted text content
   * @param extractedText - The full extracted text from a document
   * @returns A sample string for language detection
   */
  createSample(extractedText: string): string {
    return this.cleanupTextForLanguageDetection(extractedText);
  }

  /**
   * Enhanced text cleanup for language detection by removing elements that might
   * interfere with accurate language detection
   * @param text - The text to clean up
   * @returns Cleaned text more suitable for language detection
   */
  cleanupTextForLanguageDetection(text: string): string {
    if (!text) return '';

    let cleanedText = text;

    // Remove email quoted-printable encoding characters
    cleanedText = cleanedText.replace(/=\d\d/g, ' ');
    cleanedText = cleanedText.replace(/=[A-Fa-f0-9]{2}/g, ' '); // All quoted-printable encoding

    // Remove URLs
    cleanedText = cleanedText.replace(/https?:\/\/\S+/gi, ' ');
    cleanedText = cleanedText.replace(/www\.\S+/gi, ' ');
    cleanedText = cleanedText.replace(/\S+\.(com|org|net|edu|gov|io|co|us|uk|de|fr|jp|cn)\S*/gi, ' ');

    // Remove email addresses
    cleanedText = cleanedText.replace(/\S+@\S+\.\S+/gi, ' ');

    // Remove HTML tags and attributes
    cleanedText = cleanedText.replace(/<[^>]*>/g, ' ');
    cleanedText = cleanedText.replace(/&[a-z]+;/gi, ' '); // HTML entities like &nbsp;

    // Remove specific HTML entities
    cleanedText = cleanedText.replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, ' ')
      .replace(/&lt;/g, ' ')
      .replace(/&gt;/g, ' ')
      .replace(/&quot;/g, ' ')
      .replace(/&#\d+;/g, ' ');

    // Remove markdown formatting
    cleanedText = cleanedText.replace(/[*_~`#]+/g, ' ');
    cleanedText = cleanedText.replace(/\[\S*\]\(\S*\)/g, ' ');
    cleanedText = cleanedText.replace(/#{1,6}\s+.*$/gm, ' '); // Markdown headers

    // Remove file paths
    cleanedText = cleanedText.replace(/\b[A-Za-z]:\\[^\s\\/:*?"<>|]+(?:\\[^\s\\/:*?"<>|]+)*\b/g, ' ');
    cleanedText = cleanedText.replace(/\b\/(?:[^\s\\/:*?"<>|]+\/)*[^\s\\/:*?"<>|]+\b/g, ' ');

    // Remove common XML/JSON patterns
    cleanedText = cleanedText.replace(/<\/?[^>]+(>|$)/g, ' ');
    cleanedText = cleanedText.replace(/[{}"[\]]/g, ' ');

    // Remove encoded or special characters
    cleanedText = cleanedText.replace(/\\u[0-9a-fA-F]{4}/g, ' '); // Unicode escapes
    cleanedText = cleanedText.replace(/\\x[0-9a-fA-F]{2}/g, ' '); // Hex escapes

    // Remove mathematical expressions and numbers
    cleanedText = cleanedText.replace(/\b\d+\.\d+\b/g, ' '); // Decimal numbers
    cleanedText = cleanedText.replace(/\b\d+\b/g, ' '); // Integer numbers
    cleanedText = cleanedText.replace(/[-+*/=^%<>≤≥≠≈]+/g, ' '); // Math operators
    cleanedText = cleanedText.replace(/\b(0x[0-9a-fA-F]{2,})\b/g, ' '); // Hex numbers with 0x prefix
    cleanedText = cleanedText.replace(/\b([0-9a-fA-F]{8,40})\b/g, ' '); // Likely hashes (MD5, SHA, etc.)

    // Remove date and time formats
    cleanedText = cleanedText.replace(/\b\d{1,2}:\d{2}(:\d{2})?([ ]?[ap]\.?m\.?)?\b/gi, ' '); // Times: HH:MM:SS, HH:MM am/pm

    // Remove technical patterns
    cleanedText = cleanedText.replace(/\b[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}\b/gi, ' '); // UUIDs
    cleanedText = cleanedText.replace(/\b(?:[0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}\b/g, ' '); // MAC addresses
    cleanedText = cleanedText.replace(/\b(?:\d{1,3}\.){3}\d{1,3}\b/g, ' '); // IPv4 addresses
    cleanedText = cleanedText.replace(/\b([0-9a-f]{1,4}:){7}[0-9a-f]{1,4}\b/gi, ' '); // IPv6 addresses

    // Remove common programming language constructs
    cleanedText = cleanedText.replace(/\b(function|var|let|const|if|else|for|while|return|class|import|export)\b/g, ' ');
    cleanedText = cleanedText.replace(/\b[A-Za-z0-9_]+\(.*?\)/g, ' '); // Function calls

    // Remove hex color codes
    cleanedText = cleanedText.replace(/#[0-9a-fA-F]{3,6}\b/g, ' ');

    // Remove common file extensions
    cleanedText = cleanedText.replace(/\.\w{2,5}\b/g, ' '); // .pdf, .docx, .jpg, etc.

    // Remove camelCase or snake_case identifiers often found in code
    cleanedText = cleanedText.replace(/\b[a-z]+([A-Z][a-z]*)+\b/g, ' '); // camelCase
    cleanedText = cleanedText.replace(/\b[a-z]+(_[a-z]+)+\b/g, ' '); // snake_case

    // Clean up common email artifacts and excessive whitespace
    cleanedText = cleanedText.replace(/(\r\n|\n){2,}/g, ' '); // Replace multiple newlines
    cleanedText = cleanedText.replace(/[^\p{L}\p{P}\p{Z}]/gu, ' '); // Keep only letters, punctuation and spaces

    // Remove various brackets and parentheses commonly used in non-linguistic contexts
    cleanedText = cleanedText.replace(/[()[\]{}><]/g, ' ');

    // Remove special characters that don't contribute to language detection
    cleanedText = cleanedText.replace(/[^\p{L}\p{Z}.,;:!?]/gu, ' '); // Keep only letters, spaces, and basic punctuation

    // Normalize whitespace
    cleanedText = cleanedText.replace(/\s+/g, ' ').trim();

    return cleanedText;
  }

}
